import type { AreaKey } from "@/lib/areas"
import type { AppShell } from "@/App"
import type { Kind } from "@/lib/kind-labels"

// ============================================================
// Tipos
// ============================================================

export type SimulationStatus =
  | "draft"
  | "under_review"
  | "ganho"
  | "perdido"
  | "sent"

export type TariffType = "simples" | "bi_horaria" | "tri_horaria"
export type PhaseType = "monofasico" | "trifasico"

export interface BrandRow {
  brand_id: number
  brand_name: string
  logo_image: string | null
  url: string | null
  featured: boolean | null
  solar_slug: string | null
  margin_with_battery: number | string | null
  margin_without_battery: number | string | null
  is_active: boolean | null
  solar_sort_order?: number | null
  sort_order?: number | null
  solar_updated_at?: string | null
}

export interface CategoryRoleRow {
  category_id: number
  category_name: string
  description: string | null
  parent_id: number | null
  role: string | null
  is_required_in_kit: boolean | null
  conditional_flag: string | null
  sort_order: number | null
}

export interface ContractedPowerRow {
  id: string
  name: string
  value_kva: number | string
  is_active: boolean
  sort_order: number
}

export interface LeadRow {
  /** BIGINT da DB — JS perde precisão acima de 2^53. Pode vir como string. */
  id: number | string
  name: string
  email: string | null
  phone: string | null
  address: string | null
  city: string | null
  postal_code: string | null
  formatted_address?: string | null
  latitude: number | string | null
  longitude: number | string | null
  created_at: string
}

export interface LeadFormExtras {
  house_type: "moradia" | "apartamento" | "comercio" | null
  install_location: string | null
  roof_material: string | null
  roof_inclination_type: "plano" | "inclinado" | null
  average_monthly_bill_eur: number | null
  monthly_consumption_total_kwh: number | null
  raw_consumption_text: string | null
}

export interface LeadDetail extends LeadRow {
  phone_work: string | null
  owner_id: string | null
  form_extras: LeadFormExtras
}

export interface ProductRow {
  id: number
  name: string
  /** Nome público curado (à prova de sync). Nome de exibição — fallback ao name. */
  alt_name?: string | null
  sku: string | null
  description: string | null
  base_price: number | string | null
  /** Custo real do Odoo (standard_price) — base do preço do simulador (× margem). */
  cost_price: number | string | null
  brand_id: number | null
  brand_name: string | null
  brand_solar_slug: string | null
  margin_with_battery: number | string | null
  margin_without_battery: number | string | null
  images: unknown
  additional_properties: Record<string, unknown> | null
  categories: Array<{ id: number; name: string; role: string | null }> | null
  tax_rate: number | string | null
  is_active: boolean
}

/**
 * Custo unitário base do preço (× margem), coerente com o engine (kit-builder
 * effectiveCost): usa o custo real do Odoo (`cost_price`) quando >0; caso
 * contrário cai no `base_price` listado.
 */
export function effectiveCost(
  p: Pick<ProductRow, "cost_price" | "base_price">,
): number {
  const c = Number(p.cost_price ?? 0)
  return c > 0 ? c : Number(p.base_price ?? 0)
}

/**
 * Nome público de exibição: `alt_name` curado (à prova de sync), com fallback
 * ao `name` importado do Odoo. Coerente com o engine (kit-builder publicName).
 */
export function publicName(
  p: { alt_name?: string | null; name: string },
): string {
  const alt = p.alt_name?.trim()
  return alt && alt.length > 0 ? alt : p.name
}

/**
 * Roles solar SEM margem (espelha functions/lib/engine/solar-metadata.ts →
 * NO_MARGIN_ROLES): instalação e mão de obra entram a CUSTO — margem e ajuste
 * de preço (desconto/aumento) não se aplicam. AC/BC: ver MODULE_NO_MARGIN_ROLES.
 */
export const SOLAR_NO_MARGIN_ROLES: ReadonlySet<string> = new Set([
  "installation",
  "installation_backup",
  "installation_charger",
  "labour",
  // "Outros" (deslocação/custos avulsos) — só IVA, sem margem.
  "outros",
])

/**
 * Roles AC/BC SEM margem (espelha functions/lib/engine/types.ts →
 * MODULE_NO_MARGIN_ROLES): acessórios (de instalação e avulsos) e mão de obra
 * só com IVA.
 * Nos módulos o ajuste de preço incide no valor final — também nestas linhas.
 */
export const MODULE_NO_MARGIN_ROLES: ReadonlySet<string> = new Set(["installation", "labour", "accessory"])

/**
 * Preço final (c/ IVA) de uma linha AC/BC, coerente com applyMarginByLines:
 * equipamentos custo × margem; instalação/mão de obra a custo; ajuste de preço
 * (desconto > 0, aumento < 0) sobre tudo; × IVA. A soma das linhas = total.
 */
export function moduleLineFinalPrice(opts: {
  role: string | null | undefined
  cost: number
  appliedMargin: number
  vatMultiplier: number
  discountFraction: number
}): number {
  const { role, cost, appliedMargin, vatMultiplier, discountFraction } = opts
  const withMargin = role != null && MODULE_NO_MARGIN_ROLES.has(role) ? cost : cost * appliedMargin
  return withMargin * (1 - discountFraction) * vatMultiplier
}

/**
 * Multiplicador de preço por linha, coerente com o engine (computePrice):
 *   - linha COM margem: custo × margem × (1 − ajuste) × IVA efectivo
 *   - linha SEM margem: custo × IVA efectivo (sem margem, sem ajuste)
 * `discountFraction` tem sinal (>0 desconto, <0 aumento). Com este factor o
 * somatório das linhas iguala o total guardado. Para AC/BC passar isSolar=false
 * (margem aplica-se a todas as linhas).
 */
export function linePriceMultiplier(opts: {
  role: string | null | undefined
  appliedMargin: number
  effectiveVat: number
  discountFraction: number
  isSolar: boolean
}): number {
  const { role, appliedMargin, effectiveVat, discountFraction, isSolar } = opts
  const noMargin = isSolar && role != null && SOLAR_NO_MARGIN_ROLES.has(role)
  if (noMargin) return effectiveVat
  return appliedMargin * (1 - discountFraction) * effectiveVat
}

/** Uma linha de condição de pagamento: % do total + descrição. */
export interface PaymentTermLine {
  pct: number
  label: string
}

/** Preset nomeado de condições de pagamento (percentagens somam 100). */
export interface PaymentTermsPreset {
  name: string
  lines: PaymentTermLine[]
}

/** Soma das percentagens; usada para validar (tem de dar 100). */
export function paymentTermsSum(lines: PaymentTermLine[]): number {
  return lines.reduce((s, l) => s + (Number(l.pct) || 0), 0)
}

/** true quando as percentagens somam 100 (tolerância de arredondamento). */
export function paymentTermsValid(lines: PaymentTermLine[]): boolean {
  return lines.length > 0 && Math.abs(paymentTermsSum(lines) - 100) < 0.01
}

export const FALLBACK_PAYMENT_TERMS: PaymentTermLine[] = [
  { pct: 30, label: "Adjudicação" },
  { pct: 70, label: "Instalação" },
]

export interface SettingsRow {
  id: string
  default_panel_power_w: number
  min_panels: number
  max_panels: number
  coverage_with_battery: number
  coverage_without_battery: number
  production_per_kwp_year: number
  default_brand_id: number | null
  /** Marca solar dos kits do site sem proposta-modelo (E4). */
  site_kit_brand_id?: number | null
  angle_flat: number
  angle_low: number
  angle_high: number
  default_margin: number
  vat_multiplier: number
  tax_exemption_factor: number
  max_discount_fraction: number
  /** Desconto automático por baterias: a partir de N baterias, X (fração). X = 0 desliga. */
  battery_discount_min_qty?: number
  battery_discount_fraction?: number | string
  /** Quando true, Cargos não-admin (Vendedor) também podem dar desconto. */
  allow_seller_discount?: boolean
  /** Push Odoo "linha única negociada": total sem IVA na 1ª linha, resto a 0. */
  odoo_negotiated_single_line?: boolean
  /** default_code Odoo da 1ª linha (fornecimento). Default PROD981. */
  odoo_supply_product_code?: string | null
  summer_start_month: number
  summer_end_month: number
  summer_empty_consumption_factor: number
  sell_price_empty_eur_kwh: number
  sell_price_peak_eur_kwh: number
  sell_price_full_eur_kwh: number
  default_consumption_empty_fraction: number
  default_consumption_peak_fraction: number
  default_consumption_full_fraction: number
  tariff_simple_price_eur_kwh: number
  tariff_bi_empty_eur_kwh: number
  tariff_bi_peak_eur_kwh: number
  tariff_bi_full_eur_kwh: number
  tariff_tri_empty_eur_kwh: number
  tariff_tri_peak_eur_kwh: number
  tariff_tri_full_eur_kwh: number
  pvgis_loss_pct: number
  pvgis_pvtechchoice: string
  pdf_contract_model_json: unknown | null
  pdf_company_name: string | null
  pdf_company_logo_url: string | null
  pdf_footer_text: string | null
  auto_create_tag: string | null
  auto_create_default_bill_eur: number
  /** % da fatura tratada como custos fixos (retirada antes de estimar kWh). */
  estimate_fixed_cost_fraction?: number
  /** Presets de condições de pagamento; o 1º é o default na criação. */
  payment_terms_presets?: PaymentTermsPreset[] | null
  /** Ordem por role do simulador (role→nº). Ordena as linhas do kit. */
  role_order?: Record<string, number> | null
  /** Config jsonb por módulo (Fase 3+). Pode ser null se ainda não povoado. */
  ac_config?: ModuleConfig | null
  bc_aqs_config?: ModuleConfig | null
  bc_aq_config?: ModuleConfig | null
  /** CRM do Odoo: equipas e etapa (ids); vazio = pelo nome. */
  odoo_crm_config?: OdooCrmConfig | null
  /** Texto fixo do fim do email da proposta, por módulo. Vazio = texto por omissão. */
  proposal_email_texts?: { solar?: string; ac?: string; bc?: string } | null
}

export interface OdooCrmConfig {
  prospect_team_id?: number | null
  sales_team_id?: number | null
  proposal_stage_id?: number | null
}

export interface ModuleConfig {
  /** Potência por m² do dimensionamento (AC e BC aquecimento). */
  w_per_m2?: number | null
  default_margin?: number | null
  default_brand_id?: number | null
  [key: string]: unknown
}

interface OkResponse<T> {
  success: true
  data: T
  message?: string
}

interface ErrResponse {
  success: false
  error: string
  details?: unknown
}

type Response<T> = OkResponse<T> | ErrResponse

// ============================================================
// Wrappers genéricos sobre o AppClient do SDK
// ============================================================

/**
 * Erro de token dentro do envelope. A renovação é da sessão do SDK (App.tsx):
 * o CRM envia tokens novos e o client repete uma vez um 401 "token expired".
 * Se o erro chegar aqui, a próxima chamada já sai com o token renovado — por
 * isso só se mostra uma mensagem clara, sem recarregar (o reload reabria a
 * iframe com o mesmo token velho do URL e perdia o trabalho).
 */
function isTokenAuthError(error: string): boolean {
  const norm = String(error).toLowerCase()
  return (
    norm.includes("token expired") ||
    norm.includes("invalid app shell token") ||
    norm.includes("missing app token")
  )
}

function unwrap<T>(res: Response<T>): T {
  if (!res.success) {
    if (isTokenAuthError(res.error)) {
      throw new Error("A sessão estava expirada e está a ser renovada — tente de novo dentro de instantes.")
    }
    throw new Error(res.error || "Request failed")
  }
  return res.data
}

export async function fetchSettings(shell: AppShell): Promise<SettingsRow> {
  return unwrap(await shell.client.get<Response<SettingsRow>>("settings"))
}

/** Comercial atribuível a uma proposta (id = uuid guardado em created_by). */
export interface Comercial {
  id: string
  name: string | null
  email: string | null
}

/** Lista de comerciais para reatribuir propostas (só admin). */
export async function listComerciais(shell: AppShell): Promise<Comercial[]> {
  return unwrap(
    await shell.client.get<Response<{ items: Comercial[] }>>("proposal-owner"),
  ).items
}

/** Reatribui a proposta a outro comercial (só admin; auditado no servidor). */
export async function setProposalOwner(
  shell: AppShell,
  id: string,
  userId: string,
): Promise<{ id: string; created_by: string; changed: boolean }> {
  return unwrap(
    await shell.client.patch<Response<{ id: string; created_by: string; changed: boolean }>>(
      "proposal-owner",
      { id, user_id: userId },
    ),
  )
}

/** Permissões do utilizador atual, resolvidas pelo Cargo (user_roles). */
export interface MeInfo {
  user_id: string | null
  role: number | null
  role_name: string | null
  is_admin: boolean
  /** Pode aplicar desconto (admin OU allow_seller_discount ligado). */
  can_discount: boolean
  /** Equipas do utilizador no Koortex. */
  teams?: Array<{ id: number | string; slug: string | null; name: string }>
  /** Áreas do menu inicial a que tem acesso (functions/lib/areas.ts). */
  areas?: AreaKey[]
}
export async function fetchMe(shell: AppShell): Promise<MeInfo> {
  return unwrap(await shell.client.get<Response<MeInfo>>("me"))
}

// ── Catálogo AC (app-owned, gerido na Mapeamentos AC) ──────────────────
export interface AcProduct {
  id: number
  role: string
  name: string
  /** Referência do Odoo (default_code) — usada no envio da proposta. */
  sku: string | null
  odoo_name: string | null
  price_eur: number | string
  brand: string | null
  gama: string | null
  type: string | null
  btu: number | null
  cooling_kw: number | string | null
  n_ports: number | null
  product_id: number | null
  active: boolean
  sort_order: number
  /** Produto do CRM com o mesmo SKU (null: SKU vazio ou sem produto). */
  crm_product?: CrmProductSummary | null
}

/** Produto do CRM ligado pelo SKU (catálogos AC/BC): link + o que falta ao PDF. */
export interface CrmProductSummary {
  id: number
  name: string
  /** Nome alternativo do CRM — quando definido, é o nome no simulador e no PDF. */
  alt_name: string | null
  has_image: boolean
  has_datasheet: boolean
}

export async function listAcProducts(
  shell: AppShell,
  role?: string,
): Promise<AcProduct[]> {
  const qs = role ? `?role=${encodeURIComponent(role)}` : ""
  const res = await shell.client.get<Response<{ items: AcProduct[] }>>(
    `ac_products${qs}`,
  )
  return unwrap(res).items
}

export async function patchSettings(
  shell: AppShell,
  patch: Partial<SettingsRow>,
): Promise<SettingsRow> {
  return unwrap(await shell.client.patch<Response<SettingsRow>>("settings", patch))
}

export async function listBrands(shell: AppShell): Promise<BrandRow[]> {
  const res = await shell.client.get<Response<{ items: BrandRow[] }>>("brand-margins")
  return unwrap(res).items
}

export async function upsertBrandMargin(
  shell: AppShell,
  payload: {
    brand_id: number
    slug: string
    margin_with_battery: number
    margin_without_battery: number
    is_active: boolean
    sort_order: number
  },
): Promise<unknown> {
  return unwrap(await shell.client.post<Response<unknown>>("brand-margins", payload))
}

export async function deleteBrandMargin(
  shell: AppShell,
  brandId: number,
): Promise<void> {
  unwrap(
    await shell.client.delete<Response<unknown>>(`brand-margins/${brandId}`),
  )
}

export async function listCategoryRoles(
  shell: AppShell,
): Promise<CategoryRoleRow[]> {
  const res = await shell.client.get<Response<{ items: CategoryRoleRow[] }>>(
    "category-roles",
  )
  return unwrap(res).items
}

export async function upsertCategoryRole(
  shell: AppShell,
  payload: {
    category_id: number
    role: string
    is_required_in_kit: boolean
    conditional_flag: string | null
    sort_order: number
  },
): Promise<unknown> {
  return unwrap(await shell.client.post<Response<unknown>>("category-roles", payload))
}

export async function deleteCategoryRole(
  shell: AppShell,
  categoryId: number,
): Promise<void> {
  unwrap(
    await shell.client.delete<Response<unknown>>(`category-roles/${categoryId}`),
  )
}

// ── Mapeamentos (perfil de simulador: role + metadata) ───────────────────

export interface MappingProductRow {
  product_id: number
  sku: string | null
  name: string
  alt_name: string | null
  exists_in_crm: boolean
  active: boolean
  sort_order: number
  metadata: Record<string, unknown>
  // Validação: custo (Odoo) + margem (marca) + se o grupo leva margem.
  cost_price: number | null
  base_price: number | null
  brand_name: string | null
  no_margin: boolean
  margin_with_battery: number | null
  margin_without_battery: number | null
  default_margin: number | null
}

export interface MappingSearchRow {
  product_id: number
  sku: string | null
  name: string
  alt_name: string | null
  is_active: boolean
  current_role: string | null
  /** Com `catalog=ac|bc`: linhas do catálogo AC/BC já ligadas a este produto. */
  catalog_links?: Array<{ id: number; role: string }>
}

/** Produtos no role (ativos + inativos), com metadata, ordenados ativos-primeiro. */
export async function listRoleProducts(
  shell: AppShell,
  role: string,
): Promise<MappingProductRow[]> {
  const res = await shell.client.get<Response<{ items: MappingProductRow[] }>>(
    `mappings?role=${encodeURIComponent(role)}`,
  )
  return unwrap(res).items
}

/** Pesquisa no catálogo (para relacionar um produto a um role). */
export async function searchMappingProducts(
  shell: AppShell,
  query: string,
  catalog?: CatalogKind,
): Promise<MappingSearchRow[]> {
  const res = await shell.client.get<Response<{ items: MappingSearchRow[] }>>(
    `mappings?search=${encodeURIComponent(query)}${catalog ? `&catalog=${catalog}` : ""}`,
  )
  return unwrap(res).items
}

/** Catálogos app-owned dos Mapeamentos: ac_products / bc_products. */
export type CatalogKind = "ac" | "bc"

/** Linha do catálogo AC/BC nos Mapeamentos (campos técnicos variam por kind). */
export interface CatalogItem {
  id: number
  role: string
  name: string
  sku: string | null
  price_eur: number | string
  brand: string | null
  active: boolean
  archived: boolean
  /** Campos em falta para poder ativar (rótulos). */
  missing: string[]
  product_id: number | null
  crm_product: CrmProductSummary | null
  metadata?: Record<string, unknown> | null
  [field: string]: unknown
}

const catalogPath = (kind: CatalogKind) => (kind === "ac" ? "ac_products" : "bc_products")

export async function listCatalogItems(
  shell: AppShell,
  kind: CatalogKind,
  role: string,
  archived = false,
): Promise<CatalogItem[]> {
  const qs = `?role=${encodeURIComponent(role)}${archived ? "&archived=1" : ""}`
  return unwrap(await shell.client.get<Response<{ items: CatalogItem[] }>>(`${catalogPath(kind)}${qs}`)).items
}

/** «Adicionar produto ao grupo»: preço = custo do Odoo; fica inativa até completa. */
export async function createCatalogItem(
  shell: AppShell,
  kind: CatalogKind,
  role: string,
  productId: number,
): Promise<CatalogItem> {
  return unwrap(await shell.client.post<Response<CatalogItem>>(catalogPath(kind), { role, product_id: productId }))
}

/** Preço/técnicos, `product_id` (ligar; null desliga), `active`, `archived`. */
/** Resultado da ligação automática do catálogo AC/BC aos produtos do CRM. */
export interface CatalogAutoLinkResult {
  dry_run: boolean
  linked: Array<{ id: number | string; name: string; product_id: number; sku: string | null; by: "sku" | "name" }>
  ambiguous: Array<{ id: number | string; name: string; candidates: Array<{ id: number; name: string; sku: string | null }> }>
  not_found: Array<{ id: number | string; name: string; sku: string | null }>
  already: number
}

/** Liga as linhas sem produto do CRM (pela referência, senão pelo nome). `dryRun` só conta. */
export async function autoLinkCatalog(shell: AppShell, kind: CatalogKind, dryRun: boolean): Promise<CatalogAutoLinkResult> {
  return unwrap(await shell.client.post<Response<CatalogAutoLinkResult>>("catalog-autolink", { kind, dry_run: dryRun }))
}

export async function patchCatalogItem(
  shell: AppShell,
  kind: CatalogKind,
  patch: Record<string, unknown> & { id: number },
): Promise<CatalogItem> {
  return unwrap(await shell.client.patch<Response<CatalogItem>>(catalogPath(kind), patch))
}

export async function assignProductToRole(
  shell: AppShell,
  productId: number,
  role: string,
): Promise<void> {
  unwrap(
    await shell.client.post<Response<unknown>>("mappings", {
      action: "assign",
      product_id: productId,
      role,
    }),
  )
}

export async function unassignProductFromRole(
  shell: AppShell,
  productId: number,
): Promise<void> {
  unwrap(
    await shell.client.post<Response<unknown>>("mappings", {
      action: "unassign",
      product_id: productId,
    }),
  )
}

export async function setMappingProductActive(
  shell: AppShell,
  productId: number,
  active: boolean,
): Promise<void> {
  unwrap(
    await shell.client.post<Response<unknown>>("mappings", {
      action: "set_active",
      product_id: productId,
      active,
    }),
  )
}

export async function updateProductMetadata(
  shell: AppShell,
  productId: number,
  metadata: Record<string, unknown>,
): Promise<void> {
  unwrap(
    await shell.client.post<Response<unknown>>("mappings", {
      action: "update_metadata",
      product_id: productId,
      metadata,
    }),
  )
}

export interface BomRow {
  id: string
  n_panels: number
  component_product_id: number | null
  component_name: string
  component_sku: string | null
  qty: number
  sort_order: number
}

/** BOM de uma estrutura (linhas cruas + contagens de painéis existentes). */
export async function listStructureBom(
  shell: AppShell,
  productId: number,
): Promise<{ items: BomRow[]; panel_counts: number[] }> {
  const res = await shell.client.get<Response<{ items: BomRow[]; panel_counts?: number[] }>>(
    `mappings?structure_bom=${productId}`,
  )
  const data = unwrap(res)
  return { items: data.items, panel_counts: data.panel_counts ?? [] }
}

export async function setBomQty(shell: AppShell, id: string, qty: number): Promise<void> {
  unwrap(await shell.client.post<Response<unknown>>("mappings", { action: "bom_set_qty", id, qty }))
}

export async function removeBomComponent(shell: AppShell, id: string): Promise<void> {
  unwrap(await shell.client.post<Response<unknown>>("mappings", { action: "bom_remove", id }))
}

export async function addBomComponent(
  shell: AppShell,
  input: {
    structure_product_id: number
    n_panels: number
    component_product_id: number | null
    component_name: string
    component_sku: string | null
    qty: number
  },
): Promise<void> {
  unwrap(await shell.client.post<Response<unknown>>("mappings", { action: "bom_add", ...input }))
}

export async function listContractedPowers(
  shell: AppShell,
): Promise<ContractedPowerRow[]> {
  const res = await shell.client.get<Response<{ items: ContractedPowerRow[] }>>(
    "contracted-powers",
  )
  return unwrap(res).items
}

export async function upsertContractedPower(
  shell: AppShell,
  payload: {
    name: string
    value_kva: number
    is_active: boolean
    sort_order: number
  },
): Promise<unknown> {
  return unwrap(
    await shell.client.post<Response<unknown>>("contracted-powers", payload),
  )
}

export async function deleteContractedPower(
  shell: AppShell,
  id: string,
): Promise<void> {
  unwrap(
    await shell.client.delete<Response<unknown>>(`contracted-powers/${id}`),
  )
}

export async function listLeads(
  shell: AppShell,
  options?: { search?: string; limit?: number },
): Promise<LeadRow[]> {
  const params = new URLSearchParams()
  if (options?.search) params.set("search", options.search)
  if (options?.limit) params.set("limit", String(options.limit))
  const qs = params.toString()
  const res = await shell.client.get<Response<{ items: LeadRow[] }>>(
    `leads${qs ? `?${qs}` : ""}`,
  )
  return unwrap(res).items
}

// ── CRM do Odoo (leads do simulador, D1) ────────────────────────────────

export type SimModule = "solar" | "ac" | "bc_aq" | "bc_aqs"

/** Lead/oportunidade do CRM do Odoo (equipas Prospeção + Vendas). */
export interface OdooLead {
  id: number
  name: string
  type: "lead" | "opportunity"
  team: { id: number; name: string } | null
  stage: { id: number; name: string } | null
  contact_name: string | null
  partner: { id: number; name: string } | null
  email: string | null
  phone: string | null
  street: string | null
  zip: string | null
  city: string | null
  tags: string[]
  salesperson: string | null
  /** Módulo sugerido pelas etiquetas. */
  module_hint: SimModule | null
}

export async function searchOdooLeads(shell: AppShell, search: string, limit = 15): Promise<OdooLead[]> {
  const qs = new URLSearchParams({ search, limit: String(limit) })
  return unwrap(await shell.client.get<Response<{ items: OdooLead[] }>>(`odoo/leads?${qs}`)).items
}

export interface OdooCrmOptions {
  teams: Array<{ id: number; name: string }>
  stages: Array<{ id: number; name: string; is_won: boolean }>
  /** Configuração em vigor (Configurações, senão pelo nome no Odoo). */
  resolved: { prospectTeamId: number | null; salesTeamId: number | null; proposalStageId: number | null }
}

export async function getOdooCrmOptions(shell: AppShell): Promise<OdooCrmOptions> {
  return unwrap(await shell.client.get<Response<OdooCrmOptions>>("odoo/crm-options"))
}

export async function getLead(
  shell: AppShell,
  id: number | string,
): Promise<LeadDetail> {
  return unwrap(await shell.client.get<Response<LeadDetail>>(`leads/${id}`))
}

// ============================================================
// BC (bomba calor) — create actions
// ============================================================

export interface BcCreateResult {
  id: string
  number: string
  total_price_eur: number | string
}

/** Common client/address fields shared by BC AQS + BC AQ create payloads. */
export interface BcClientPayload {
  lead_id?: number | string | null
  odoo_lead_id?: number | null
  odoo_lead_name?: string | null
  lead_origin?: string | null
  lead_origin_detail?: string | null
  entity_id?: string | null
  brand_id?: number | null
  client_name: string
  client_email?: string | null
  client_phone?: string | null
  address?: string | null
  city?: string | null
  postal_code?: string | null
  latitude?: number | null
  longitude?: number | null
  notes?: string | null
}

export async function createBcAqs(
  shell: AppShell,
  payload: BcClientPayload & {
    occupants: number
    /** Nível dos acessórios de instalação (1–4). Default 1. */
    install_level?: number
    brand?: string | null
    /** Horas de mão de obra; sem valor usa as 6h por defeito. */
    labour_hours?: number | null
  },
): Promise<BcCreateResult> {
  const res = await shell.client.post<{
    success: boolean
    data?: BcCreateResult
    error?: string
  }>("actions/bc_aqs_create", { params: payload })
  if (!res.success || !res.data) throw new Error(res.error || "Falha a criar BC AQS")
  return res.data
}

export async function createBcAq(
  shell: AppShell,
  payload: BcClientPayload & {
    heated_area_m2: number
    phase?: "mono" | "tri" | null
    /** 'aq' = só aquecimento (inércia) · 'aq_aqs' = + águas quentes (termoacumulador). */
    mode?: "aq" | "aq_aqs" | null
    /** Nível dos acessórios de instalação (1–3). Default 1. */
    install_level?: number
    /** false quando o cliente já tem depósito instalado. */
    include_tank?: boolean
    tank_product_id?: number | null
    brand?: string | null
    /** Horas de mão de obra; sem valor usa as do nível (16/24/36). */
    labour_hours?: number | null
  },
): Promise<BcCreateResult> {
  const res = await shell.client.post<{
    success: boolean
    data?: BcCreateResult
    error?: string
  }>("actions/bc_aq_create", { params: payload })
  if (!res.success || !res.data) throw new Error(res.error || "Falha a criar BC AQ")
  return res.data
}

export async function createAc(
  shell: AppShell,
  payload: BcClientPayload & {
    divisions: Array<{ name: string; area_m2: number }>
    topology?: "auto" | "mono" | "multi"
    phase?: "mono" | "tri" | null
  },
): Promise<BcCreateResult> {
  const res = await shell.client.post<{
    success: boolean
    data?: BcCreateResult
    error?: string
  }>("actions/ac_create", { params: payload })
  if (!res.success || !res.data) throw new Error(res.error || "Falha a criar AC")
  return res.data
}

/** Edita um orçamento AC/BC existente (re-corre o motor). Genérico via actions/update. */
export async function updateAc(
  shell: AppShell,
  id: string,
  payload: BcClientPayload & {
    divisions: Array<{ name: string; area_m2: number }>
    topology?: "auto" | "mono" | "multi"
    phase?: "mono" | "tri" | null
  },
): Promise<BcCreateResult> {
  const res = await shell.client.post<{
    success: boolean
    data?: BcCreateResult
    error?: string
  }>("actions/update", { params: { id, ...payload } })
  if (!res.success || !res.data) throw new Error(res.error || "Falha a guardar")
  return res.data
}

/** Edita um orçamento AC/BC (genérico) — re-corre o motor via actions/update. */
export async function updateModuleProposal(
  shell: AppShell,
  id: string,
  payload: Record<string, unknown>,
): Promise<BcCreateResult> {
  const res = await shell.client.post<{
    success: boolean
    data?: BcCreateResult
    error?: string
  }>("actions/update", { params: { id, ...payload } })
  if (!res.success || !res.data) throw new Error(res.error || "Falha a guardar")
  return res.data
}

export async function geocodeAddress(
  shell: AppShell,
  address: string,
): Promise<{
  latitude: number
  longitude: number
  formatted_address?: string
}> {
  return unwrap(
    await shell.client.post<
      Response<{ latitude: number; longitude: number; formatted_address?: string }>
    >("geocode", { address }),
  )
}

/** Tenant's Google Maps JS API key (or null when not configured). */
export async function getGoogleMapsConfig(
  shell: AppShell,
): Promise<{ enabled: boolean; apiKey: string | null }> {
  return unwrap(
    await shell.client.get<Response<{ enabled: boolean; apiKey: string | null }>>(
      "google-maps-config",
    ),
  )
}

// ============================================================
// Proposals (multi-module — solar / ac / bc_aqs / bc_aq)
// ============================================================

export interface ProposalListRow {
  id: string
  number: string
  /** Tipo de módulo da proposta. Default `solar` para registos pré-migração. */
  kind: Kind
  lead_id: number | null
  status: SimulationStatus
  client_name: string
  client_email: string | null
  brand_id: number | null
  panel_quantity: number | null
  panel_power_w: number | null
  installed_power_kwp: number | string | null
  total_price_eur: number | string | null
  annual_savings_eur: number | string | null
  payback_years: number | string | null
  has_battery: boolean
  has_backup: boolean
  created_at: string
  updated_at: string
  /** Autor da proposta (uuid). Usado no filtro por vendedor (admins). */
  created_by?: string | null
  /** Nome do vendedor (só resolvido para admins). */
  created_by_name?: string | null
  /** Quem gerou a proposta: `user` (comercial) ou `ai` (formulário do site). */
  generated_by?: GeneratedBy
}

/** Quem gerou a proposta (`proposals.generated_by`). */
export type GeneratedBy = "user" | "ai"

export interface ProposalKpis {
  total: number
  this_month: number
  avg_price: number
  total_savings: number
  /** Por módulo, sobre TODAS as propostas visíveis (não só a página devolvida). */
  by_kind?: Partial<Record<Kind, { total: number; this_month: number }>>
  /** Vendedores das propostas visíveis (nomes só para admins). */
  sellers?: Array<{ id: string; name: string }>
}

/** Aliases legados — mantém imports antigos a compilar. */
export type SimulationListRow = ProposalListRow
export type SimulationKpis = ProposalKpis

export async function listProposals(
  shell: AppShell,
  options?: {
    kind?: Kind
    status?: SimulationStatus
    search?: string
    lead_id?: number
    brand_id?: number
    generated_by?: GeneratedBy
    /** Vendedor (uuid) — filtrado no servidor. */
    created_by?: string
    limit?: number
    include_kpis?: boolean
  },
): Promise<{ items: ProposalListRow[]; kpis?: ProposalKpis }> {
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(options ?? {})) {
    if (v !== undefined && v !== null && v !== "") params.set(k, String(v))
  }
  const qs = params.toString()
  const res = await shell.client.get<
    Response<{ items: ProposalListRow[]; kpis?: ProposalKpis }>
  >(`proposals${qs ? `?${qs}` : ""}`)
  const data = unwrap(res)
  // Defensive: backend pré-migração pode não devolver `kind`. Default = solar.
  return {
    ...data,
    items: data.items.map((it) => ({ ...it, kind: (it.kind ?? "solar") as Kind })),
  }
}

/** Compat alias — chamadas antigas continuam a funcionar. */
export const listSimulations = listProposals

export async function getProposal(
  shell: AppShell,
  id: string,
): Promise<Record<string, unknown>> {
  return unwrap(
    await shell.client.get<Response<Record<string, unknown>>>(`proposals/${id}`),
  )
}

/** Compat alias. */
export const getSimulation = getProposal

export interface CreateSimulationPayload {
  /** BIGINT — passar como string para preservar precisão acima de 2^53. */
  lead_id?: number | string | null
  /** Lead do CRM do Odoo escolhida no assistente. */
  odoo_lead_id?: number | null
  odoo_lead_name?: string | null
  /** Origem da lead (passo 1). */
  lead_origin?: string | null
  lead_origin_detail?: string | null
  client_name: string
  client_email?: string | null
  client_phone?: string | null
  address?: string | null
  city?: string | null
  postal_code?: string | null
  latitude: number
  longitude: number
  house_type?: string | null
  roof_material?: string | null
  roof_inclination_type?: string | null
  mounting_structure_product_id?: number | null
  tariff_type: TariffType
  phase_type: PhaseType
  contracted_power_id: string
  has_invoice: boolean
  average_monthly_bill_eur?: number | null
  monthly_consumption_total_kwh?: number | null
  consumption_distribution_empty?: number | null
  consumption_distribution_peak?: number | null
  consumption_distribution_full?: number | null
  consumption_months_json?: Array<{
    month?: number | null
    empty: number
    peak: number
    full: number
  }> | null
  has_charger: boolean
  has_meter: boolean
  has_battery: boolean
  has_backup: boolean
  backup_pipe_length_m?: number | null
  brand_id: number
  panel_power_w?: number | null
  discount_fraction?: number
  notes?: string | null
}

export async function createSolarProposal(
  shell: AppShell,
  payload: CreateSimulationPayload,
): Promise<{
  id: string
  number: string
  total_price_eur: number | string
  payback_years: number | string
  annual_savings_eur: number | string
}> {
  return unwrap(
    await shell.client.post<
      Response<{
        id: string
        number: string
        total_price_eur: number | string
        payback_years: number | string
        annual_savings_eur: number | string
      }>
    >("actions/solar_create", { params: payload }),
  )
}

/** Compat alias para o wizard solar existente. */
export const createSimulation = createSolarProposal

// ============================================================
// Products (catálogo solar)
// ============================================================

export async function listProducts(
  shell: AppShell,
  options?: {
    role?: string
    brand_slug?: string
    brand_id?: number
    search?: string
    panel_power_w?: number
    phase?: "mono" | "tri"
    contracted_power_kva?: number
    limit?: number
    /** Para AC/BC: filtra por categorias do módulo + additional_properties.<kind>.role. */
    kind?: Kind
    /** Para AC/BC: o role guardado em additional_properties.<kind>.role. */
    metadata_role?: string
    /** Para AC/BC: marca da proposta — só equipamentos dessa marca (sem marca passam). */
    brand?: string
  },
): Promise<ProductRow[]> {
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(options ?? {})) {
    if (v !== undefined && v !== null && v !== "") params.set(k, String(v))
  }
  const qs = params.toString()
  const res = await shell.client.get<Response<{ items: ProductRow[] }>>(
    `products${qs ? `?${qs}` : ""}`,
  )
  return unwrap(res).items
}

// ── Acesso por área (menu inicial) — só admins ─────────────────────────
export interface AreaAccessInfo {
  areas: AreaKey[]
  area_teams: Record<string, string[]>
  /** null = o host recusou ler as equipas (falta `teams.read`). */
  teams: Array<{
    id: number | string
    slug: string | null
    name: string
    is_active: boolean
    members: number
  }> | null
}
/** Proposta-modelo de um kit do site (Configurações → «Kits do site», E4). */
export interface SiteKitRow {
  kit_ref: string
  proposal_id: string
  number: string
  kind: Kind
  client_name: string | null
  brand_id: number | null
  total_price_eur: number | null
  lines: number
}

export async function listSiteKits(shell: AppShell): Promise<SiteKitRow[]> {
  return unwrap(await shell.client.get<Response<{ items: SiteKitRow[] }>>("site-kits")).items
}
export async function setSiteKit(shell: AppShell, kitRef: string, number: string): Promise<{ kit_ref: string; number: string }> {
  return unwrap(await shell.client.post<Response<{ kit_ref: string; number: string }>>("site-kits", { kit_ref: kitRef, number }))
}
export async function removeSiteKit(shell: AppShell, kitRef: string): Promise<void> {
  unwrap(await shell.client.delete<Response<unknown>>(`site-kits?kit_ref=${encodeURIComponent(kitRef)}`))
}

export async function fetchAreaAccess(shell: AppShell): Promise<AreaAccessInfo> {
  return unwrap(await shell.client.get<Response<AreaAccessInfo>>("area-access"))
}
export async function saveAreaAccess(
  shell: AppShell,
  areaTeams: Record<string, string[]>,
): Promise<{ area_teams: Record<string, string[]> }> {
  return unwrap(
    await shell.client.patch<Response<{ area_teams: Record<string, string[]> }>>(
      "area-access",
      { area_teams: areaTeams },
    ),
  )
}
